import type { ReactNode } from 'react'
import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { tokens, shortScreen } from '@configuration/theme'
import AlertIcon from '@icons/Alert.icon'

// Shared style for the inner control (<input>/<select>). Exported so both
// TextField and Select render an identically-styled control without a separate
// styles module. The control strips its own border/outline — the wrapper owns
// the frame, hover, and focus ring.
export const fieldControlSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  border: 0,
  outline: 0,
  background: 'transparent',
  fontFamily: 'inherit',
  fontWeight: 400,
  fontSize: 15,
  lineHeight: 1,
  color: tokens.businessBlack,
  padding: '0 14px',
  height: '100%',
  '&::placeholder': { color: tokens.businessBlack40 },
}

// Derive the id of the error region from the field id so the control can point
// at it via aria-describedby. Single source of truth shared by Field and its
// callers (TextField/Select).
export const fieldErrorId = (id: string | undefined): string | undefined =>
  id ? `${id}-error` : undefined

export interface FieldProps {
  /** Wired to the label's `htmlFor`; the control passes the same id. */
  id?: string
  label?: ReactNode
  /** Right-aligned slot in the label row (e.g. a "Forgot?" link). */
  hint?: ReactNode
  /** Validation error — switches the frame to the red skin and shows the message. */
  error?: string | null
  /** Helper text shown below the field when there is no error. */
  desc?: ReactNode
  /** Leading icon inside the frame. */
  icon?: ReactNode
  /** Trailing slot inside the frame (e.g. a password show/hide toggle). */
  trailing?: ReactNode
  /** Greys the frame to signal a non-editable, display-only control. */
  readOnly?: boolean
  /** The control element — an <input> or <select> styled with `fieldControlSx`. */
  children: ReactNode
  dataTestId?: string
}

// The visual frame shared by TextField and Select: optional label + hint row,
// a bordered input-wrap (hover/focus-within violet ring, red error skin) with
// leading-icon and trailing slots, plus helper/error text. The control itself
// is passed in as `children`.
const Field = ({
  id,
  label,
  hint,
  error,
  desc,
  icon,
  trailing,
  readOnly,
  children,
  dataTestId = 'field',
}: FieldProps) => (
  <Box data-testid={dataTestId} sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    {label && (
      <Box
        component='label'
        htmlFor={id}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 12,
          letterSpacing: '0.3px',
          color: tokens.businessBlack60,
        }}
      >
        <span>{label}</span>
        {hint && <span>{hint}</span>}
      </Box>
    )}
    <Box
      data-error={error ? '1' : '0'}
      data-readonly={readOnly ? '1' : '0'}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        height: 44,
        [shortScreen.tight]: { height: 40 },
        border: `1px solid ${tokens.borderDefault}`,
        borderRadius: tokens.radiusMd,
        background: tokens.white,
        transition: 'border-color .2s, box-shadow .2s',
        '&:hover': { borderColor: tokens.businessBlack60 },
        '&:focus-within': {
          borderColor: tokens.versatileViolet,
          boxShadow: `0 0 0 3px ${tokens.versatileViolet10}`,
        },
        '&[data-error="1"]': {
          borderColor: tokens.rebelliousRed,
          background: tokens.rebelliousRed5,
        },
        // Display-only control: greyed surface, no interactive border emphasis.
        '&[data-readonly="1"]': {
          background: tokens.businessBlack5Solid,
          '& > input': {
            color: tokens.businessBlack60,
          },
          '&:focus': {
            border: `1px solid ${tokens.borderDefault}`,
            boxShadow: 'none',
            outline: 'none',
          },
          '&:focus-within': {
            border: `1px solid ${tokens.borderDefault}`,
            boxShadow: 'none',
            outline: 'none',
          },
        },
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'inline-flex',
            paddingLeft: '14px',
            color: tokens.businessBlack40,
            pointerEvents: 'none',
          }}
        >
          {icon}
        </Box>
      )}
      {children}
      {trailing}
    </Box>
    {desc && !error && <Box sx={{ fontSize: 12, color: tokens.businessBlack40 }}>{desc}</Box>}
    {error && (
      <Box
        id={fieldErrorId(id)}
        // role="alert" so screen readers announce the validation error when it
        // appears after a submit round-trip; the control links here via
        // aria-describedby (see TextField/Select).
        role='alert'
        sx={{
          fontSize: 12,
          color: tokens.rebelliousRed,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Box component='span' aria-hidden='true' sx={{ display: 'inline-flex' }}>
          <AlertIcon />
        </Box>{' '}
        {error}
      </Box>
    )}
  </Box>
)

export default Field
